"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { L as Link, useDesign } from "@/components/design";
import { bySlug, catLabel, cutout, rub, type Product } from "@/lib/catalog";
import { cart } from "../cart";
import { VariantPick } from "./ProductCard";
import { compare } from "./sets";
import Stars from "./Stars";

const QV_EVENT = "nii:quickview";
export const openQuickView = (slug: string) => window.dispatchEvent(new CustomEvent(QV_EVENT, { detail: slug }));

// Слой магазина во всех дизайнах: быстрый просмотр товара и панель сравнения.
export default function ShopLayer() {
  return (
    <>
      <QuickView />
      <CompareBar />
    </>
  );
}

function QuickView() {
  const dialog = useRef<HTMLDialogElement>(null);
  const [p, setP] = useState<Product | null>(null);
  const [vi, setVi] = useState(0);
  const [img, setImg] = useState(0);
  const [added, setAdded] = useState(false);
  const { design } = useDesign();

  useEffect(() => {
    const open = (e: Event) => {
      const found = bySlug((e as CustomEvent<string>).detail);
      if (!found) return;
      setP(found);
      setVi(0);
      setImg(0);
      setAdded(false);
      dialog.current?.showModal();
    };
    window.addEventListener(QV_EVENT, open);
    return () => window.removeEventListener(QV_EVENT, open);
  }, []);

  const images = p ? (design === "gallery" ? [cutout(p), ...p.images.map((i) => i.src)] : p.images.map((i) => i.src)) : [];
  const v = p?.variants[vi];

  return (
    <dialog ref={dialog} className="modal qv" aria-labelledby="qv-title" onClick={(e) => e.target === e.currentTarget && dialog.current?.close()}>
      <button type="button" className="x" aria-label="Закрыть" onClick={() => dialog.current?.close()}>
        ×
      </button>
      {p && v && (
        <div className="qv__in">
          <div className="qv__gallery">
            <Image src={images[img]} alt={p.name} width={900} height={900} sizes="(max-width: 760px) 90vw, 440px" />
            <div className="qv__thumbs">
              {images.slice(0, 4).map((src, i) => (
                <button key={src} type="button" aria-label={`Фото ${i + 1}`} aria-pressed={img === i} onClick={() => setImg(i)}>
                  <Image src={src} alt="" width={120} height={120} sizes="64px" />
                </button>
              ))}
            </div>
          </div>
          <div className="qv__info">
            <p className="label qv__cat">{catLabel(p.category)}</p>
            <h2 id="qv-title">{p.name}</h2>
            <p className="card__rate">
              <Stars value={p.rating} /> <span>{p.rating.toFixed(1).replace(".", ",")}</span> <small>· {p.ratingCount} отзывов</small>
            </p>
            <p className="qv__lead">{p.lead}</p>
            {p.variants.length > 1 && <p className="label qv__cat">{p.variantLabel ?? "Вариант"}</p>}
            <VariantPick p={p} value={vi} onChange={(i) => (setVi(i), setAdded(false))} id="qv-variant" />
            <p className="qv__price">
              {rub(v.price)}
              {v.oldPrice && <s>{rub(v.oldPrice)}</s>}
            </p>
            <div className="actions">
              <button
                type="button"
                className="btn btn-p label"
                onClick={() => {
                  cart.add(p.slug, v.name);
                  setAdded(true);
                }}
              >
                {added ? "Добавлено" : "В корзину"}
              </button>
              {added ? (
                <button type="button" className="btn btn-o label" onClick={() => (dialog.current?.close(), cart.open())}>
                  Открыть корзину
                </button>
              ) : (
                <Link className="btn btn-o label" href={`/catalog/${p.slug}`} onClick={() => dialog.current?.close()}>
                  Подробнее о товаре
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </dialog>
  );
}

const ROWS: [string, (p: Product) => string][] = [
  ["Цена", (p) => (p.variants.length > 1 ? `от ${rub(Math.min(...p.variants.map((v) => v.price)))}` : rub(p.variants[0].price))],
  ["Рейтинг", (p) => `${p.rating.toFixed(1).replace(".", ",")} · ${p.ratingCount} отзывов`],
  ["Раздел", (p) => catLabel(p.category)],
  ["Вариантов", (p) => String(p.variants.length)],
  ["На складе", (p) => (p.stock > 10 ? "Много" : `Осталось ${p.stock}`)],
  ["Главное", (p) => p.features[0]?.title ?? "—"],
  ["Птенец в комплекте", () => "Нет"],
];

function CompareBar() {
  const list = compare.use();
  const dialog = useRef<HTMLDialogElement>(null);
  const items = list.map(bySlug).filter((p): p is Product => !!p);
  const { design } = useDesign();

  return (
    <>
      {items.length > 0 && (
        <div className="cmpbar" role="region" aria-label="Сравнение товаров">
          <span className="cmpbar__thumbs" aria-hidden="true">
            {items.map((p) => (
              <Image key={p.slug} src={design === "gallery" ? cutout(p) : p.images[0].src} alt="" width={80} height={80} sizes="40px" />
            ))}
          </span>
          <span className="label">Сравнение: {items.length} из 4</span>
          <button type="button" className="btn btn-p label" disabled={items.length < 2} onClick={() => dialog.current?.showModal()}>
            {items.length < 2 ? "Добавьте ещё" : "Сравнить"}
          </button>
          <button type="button" className="cmpbar__clear label" onClick={() => compare.clear()}>
            Очистить
          </button>
        </div>
      )}
      <dialog ref={dialog} className="modal cmp" aria-labelledby="cmp-title" onClick={(e) => e.target === e.currentTarget && dialog.current?.close()}>
        <button type="button" className="x" aria-label="Закрыть" onClick={() => dialog.current?.close()}>
          ×
        </button>
        <h2 id="cmp-title">Сравнение снаряжения</h2>
        <div className="cmp__scroll">
          <table className="cmp__table">
            <thead>
              <tr>
                <th scope="col">
                  <span className="sr">Параметр</span>
                </th>
                {items.map((p) => (
                  <th key={p.slug} scope="col">
                    <Image src={design === "gallery" ? cutout(p) : p.images[0].src} alt="" width={160} height={160} sizes="96px" />
                    <Link href={`/catalog/${p.slug}`} onClick={() => dialog.current?.close()}>
                      {p.name}
                    </Link>
                    <button type="button" className="cmpbar__clear label" onClick={() => compare.toggle(p.slug)}>
                      Убрать
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([name, get]) => (
                <tr key={name}>
                  <th scope="row">{name}</th>
                  {items.map((p) => (
                    <td key={p.slug}>{get(p)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </dialog>
    </>
  );
}
